'use client';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { useSearch } from './useSearch';
import { SearchResults } from './SearchResults';
import { clarityEvent } from '@/lib/clarity';
import './search.scss';

const PLACEHOLDERS = [
    'Tripod',
    'Microphone',
    'Mobile Charger',
    'Smart Switch',
    'Smart Plug',
    'LED Strip',
    'CCTV Camera',
    'Smart Bulb',
    'Door Lock',
    'Smart Fan',
];

export const SearchBar = () => {
    const router = useRouter();
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const mobileInputRef = useRef(null);

    const [query, setQuery] = useState('');
    const [focused, setFocused] = useState(false);
    const [tickerKey, setTickerKey] = useState(0);
    const [tickerIdx, setTickerIdx] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);

    const { data, loading } = useSearch(query);
    const showPanel = focused && query.trim().length >= 2;
    const showMobileResults = mobileOpen && query.trim().length >= 2;

    /* ── ticker ──────────────────────────────────── */
    useEffect(() => {
        const t = setInterval(() => {
            setTickerIdx(p => (p + 1) % PLACEHOLDERS.length);
            setTickerKey(p => p + 1);
        }, 2600);
        return () => clearInterval(t);
    }, []);

    /* ── clear-search event ──────────────────────── */
    useEffect(() => {
        const clear = () => { setQuery(''); setFocused(false); setMobileOpen(false); };
        window.addEventListener('ennix:clear-search', clear);
        return () => window.removeEventListener('ennix:clear-search', clear);
    }, []);

    /* ── click outside (desktop) ─────────────────── */
    useEffect(() => {
        const onDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setFocused(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    /* ── auto-focus mobile input ─────────────────── */
    useEffect(() => {
        if (mobileOpen) setTimeout(() => mobileInputRef.current?.focus(), 60);
    }, [mobileOpen]);

    /* ── lock body scroll when mobile overlay open ─ */
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const closePanel = () => setFocused(false);

    const closeMobile = () => { setMobileOpen(false); setQuery(''); setFocused(false); };

    const handleSubmit = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (q) {
            clarityEvent('Search');
            router.push(`/search?q=${encodeURIComponent(q)}`);
            closePanel();
            closeMobile();
        }
    };

    const handleSuggestion = (text) => {
        setQuery(text);
        clarityEvent('Search');
        router.push(`/search?q=${encodeURIComponent(text)}`);
        closePanel();
        closeMobile();
    };

    /* ── Both mobile and desktop rendered; CSS media queries control visibility ─── */
    return (
        <>
            {/* Mobile icon button — visible only on mobile via CSS */}
            <button
                type="button"
                className="search-mobile-icon-btn search-mobile-only"
                onClick={() => setMobileOpen(true)}
                aria-label="Search"
            >
                <i className="pi pi-search" style={{ fontSize: '1.35rem', height: '1.75rem', color: '#6b7280' }} />
                <span style={{
                    fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.04em',
                    textTransform: 'uppercase', color: '#6b7280', lineHeight: 1,
                }}>Search</span>
            </button>

            {/* Desktop search — visible only on desktop via CSS */}
            <div ref={wrapperRef} className="search-bar-root search-desktop-only">
                <form onSubmit={handleSubmit} className="search-bar-form" role="search" noValidate>
                    <div className="search-form-inner">
                        <IconField iconPosition="left">
                            <InputIcon className="pi pi-search search-icon" />
                            <InputText
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onFocus={() => setFocused(true)}
                                onKeyDown={(e) => { if (e.key === 'Escape') { setQuery(''); closePanel(); } }}
                                className="search-input"
                                aria-label="Search products"
                                autoComplete="off"
                            />
                        </IconField>

                        {!query && (
                            <div className="search-placeholder-overlay" aria-hidden="true">
                                <span className="placeholder-static">Search for&nbsp;</span>
                                <div className="placeholder-ticker">
                                    <span key={tickerKey} className="ticker-item">
                                        {PLACEHOLDERS[tickerIdx]}
                                    </span>
                                </div>
                            </div>
                        )}

                        {query && (
                            <button
                                type="button"
                                className="search-close-btn"
                                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                                aria-label="Clear"
                            >
                                <i className="pi pi-times" />
                            </button>
                        )}
                    </div>
                </form>

                {showPanel && (
                    <SearchResults
                        data={data}
                        loading={loading}
                        query={query}
                        onSuggestionClick={handleSuggestion}
                        onClose={closePanel}
                    />
                )}
            </div>

            {/* Mobile overlay — only mounted when open */}
            {mobileOpen && (
                <div className="mobile-search-overlay">
                    <form onSubmit={handleSubmit} className="mobile-search-bar" role="search" noValidate>
                        <button
                            type="button"
                            className="mobile-back-btn"
                            onClick={closeMobile}
                            aria-label="Close search"
                        >
                            <i className="pi pi-arrow-left" />
                        </button>

                        <div className="search-form-inner mobile-input-wrap">
                            <IconField iconPosition="left">
                                <InputIcon className="pi pi-search search-icon" />
                                <InputText
                                    ref={mobileInputRef}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Escape' && closeMobile()}
                                    className="search-input"
                                    aria-label="Search products"
                                    autoComplete="off"
                                />
                            </IconField>

                            {!query && (
                                <div className="search-placeholder-overlay" aria-hidden="true">
                                    <span className="placeholder-static">Search for&nbsp;</span>
                                    <div className="placeholder-ticker">
                                        <span key={tickerKey} className="ticker-item">
                                            {PLACEHOLDERS[tickerIdx]}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {query && (
                                <button
                                    type="button"
                                    className="search-close-btn"
                                    onClick={() => { setQuery(''); mobileInputRef.current?.focus(); }}
                                    aria-label="Clear"
                                >
                                    <i className="pi pi-times" />
                                </button>
                            )}
                        </div>
                    </form>

                    {showMobileResults && (
                        <div className="mobile-search-results">
                            <SearchResults
                                data={data}
                                loading={loading}
                                query={query}
                                onSuggestionClick={handleSuggestion}
                                onClose={closeMobile}
                                inline
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
